import { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { getAudit, unlockAudit, getProfile, saveProfile } from "@/lib/api";
import { notifyLead } from "@/lib/slack";
import { fmtMoney, fmtNum } from "@/lib/format";
import { ScoreRing } from "@/components/ScoreRing";
import { Eyebrow, TopBar, Footer } from "@/components/SiteChrome";
import ResultsView from "@/pages/ResultsView";

const Centered = ({ children }) => (
  <div className="min-h-screen bg-white flex flex-col">
    <TopBar />
    <main className="flex-1 flex items-center justify-center px-6 py-16">{children}</main>
    <Footer />
  </div>
);

const FileConfirmation = ({ fileResult }) => {
  if (!fileResult?.rows) return null;
  const { searchTerms, targets, products } = fileResult.rows;
  return (
    <div className="mt-6 inline-flex items-center gap-2 border-2 border-[color:var(--ink)] bg-[color:var(--green-soft)] px-4 py-2 ss-mono text-xs text-[color:var(--ink)]" data-testid="file-confirmation">
      <CheckCircle2 size={14} className="text-[color:var(--green)]" />
      Analysed <span className="font-semibold tabular-nums">{fmtNum(searchTerms)}</span> search terms across{" "}
      <span className="font-semibold tabular-nums">{fmtNum(targets)}</span> targets and{" "}
      <span className="font-semibold tabular-nums">{fmtNum(products)}</span> products.
    </div>
  );
};

const BlurredPreview = () => (
  <div className="mt-14 relative select-none pointer-events-none" aria-hidden="true" data-testid="blurred-preview">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="border-2 border-[color:var(--ink)]/20 p-5 bg-[color:var(--panel)]">
          <div className="h-3 w-24 bg-[color:var(--ink)]/15 mb-3" />
          <div className="h-6 w-40 bg-[color:var(--ink)]/25 mb-2" />
          <div className="h-3 w-full bg-[color:var(--ink)]/10 mb-1" />
          <div className="h-3 w-3/4 bg-[color:var(--ink)]/10" />
        </div>
      ))}
    </div>
    <div className="absolute inset-0" style={{ backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} />
    <div className="absolute inset-0 bg-white/40" />
  </div>
);

const Gate = ({ auditId, summary, fileResult, brandName, onUnlocked }) => {
  const profile = getProfile();
  const [form, setForm] = useState({
    firstName: profile?.firstName || "",
    email: profile?.email || "",
  });
  const [busy, setBusy] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [error, setError] = useState(null);
  const cur = summary.currency;

  const submit = async (e) => {
    e.preventDefault();
    setEmailError(null);
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setEmailError("Please enter a valid work email.");
      return;
    }
    setBusy(true);
    try {
      const res = await unlockAudit(auditId, {
        email: form.email,
        firstName: form.firstName || undefined,
        brandName: brandName || undefined,
      });
      saveProfile({ email: form.email, firstName: form.firstName, brandName });
      notifyLead({
        audit: res.audit,
        firstName: form.firstName,
        email: form.email,
        brandName,
      });
      onUnlocked(res.audit);
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === "INVALID_EMAIL") {
        setEmailError(data.message || "Please enter a valid work email.");
      } else {
        setError("Something went wrong opening your scorecard. Try again — or email hello@sellersside.com and we'll send it over manually.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <TopBar />
      <main className="flex-1 mx-auto max-w-3xl w-full px-6 py-16" data-testid="email-gate">
        <div className="text-center">
          <Eyebrow className="mb-5 justify-center">Your scorecard is ready</Eyebrow>
          <div className="flex justify-center mb-6">
            <ScoreRing value={summary.accountScore} size={140} />
          </div>
          <h1 className="font-display text-3xl sm:text-5xl leading-tight text-[color:var(--ink)]" data-testid="gate-headline">
            <span className="text-[color:var(--green)]">{fmtMoney(cur, summary.monthlyProfitImpact)}/month</span>
            <br />in profit improvements found
          </h1>
          <p className="mt-4 ss-mono text-sm uppercase tracking-[0.14em] text-[color:var(--grey)]">
            {summary.findingsCount} findings · account score {summary.accountScore}/100
          </p>
          <FileConfirmation fileResult={fileResult} />
        </div>

        <form onSubmit={submit} noValidate className="mt-10 text-left space-y-5 ss-panel ss-panel-shadow p-8 max-w-lg mx-auto">
          <p className="ss-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--green)]">
            Unlock the detail
          </p>
          <h2 className="font-display text-2xl text-[color:var(--ink)] -mt-3">
            Where the {fmtMoney(cur, summary.monthlyProfitImpact)}/mo is hiding
          </h2>
          <div>
            <label className="ss-label">First name (optional)</label>
            <input
              type="text"
              value={form.firstName}
              onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              className="ss-input"
              data-testid="gate-firstname-input"
            />
          </div>
          <div>
            <label className="ss-label">Work email *</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={`ss-input ${emailError ? "ss-input--error" : ""}`}
              data-testid="gate-email-input"
            />
            {emailError && (
              <p className="ss-mono text-xs text-[color:var(--bad)] mt-2" data-testid="gate-email-error">{emailError}</p>
            )}
          </div>
          {error && <p className="ss-mono text-xs text-[color:var(--bad)]" data-testid="gate-error">{error}</p>}
          <button type="submit" disabled={busy} className="ss-btn-primary w-full" data-testid="gate-submit-btn">
            {busy && <Loader2 size={16} className="animate-spin" />} Open my scorecard →
          </button>
          <p className="ss-mono text-[11px] uppercase tracking-[0.1em] text-[color:var(--grey)] text-center">
            We'll email you a copy. No spam, no obligation.
          </p>
        </form>

        <BlurredPreview />
      </main>
      <Footer />
    </div>
  );
};

const Expired = () => {
  const navigate = useNavigate();
  return (
    <Centered>
      <div className="text-center max-w-md" data-testid="expired-state">
        <Eyebrow className="mb-5 justify-center">Scorecard not found</Eyebrow>
        <h1 className="font-display text-3xl sm:text-4xl text-[color:var(--ink)]">
          This scorecard has expired
        </h1>
        <p className="mt-4 text-[color:var(--grey)]">Run a fresh one — it takes 60 seconds.</p>
        <button
          onClick={() => navigate("/")}
          className="ss-btn-primary mt-8"
          data-testid="expired-rerun-btn"
        >
          Run a fresh scorecard
        </button>
      </div>
    </Centered>
  );
};

export default function AuditPage() {
  const { auditId } = useParams();
  const location = useLocation();
  const [phase, setPhase] = useState(location.state?.summary ? "gate" : "loading");
  const [summary, setSummary] = useState(location.state?.summary || null);
  const [fileResult, setFileResult] = useState(location.state?.fileResult || null);
  const [audit, setAudit] = useState(null);
  const [brandName, setBrandName] = useState(
    location.state?.brandName || getProfile()?.brandName || ""
  );
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    // If we came from upload with a summary already, no need to fetch.
    if (location.state?.summary) return;

    // Fall back: hydrate from sessionStorage (page refresh) or GET.
    const cachedSummary = sessionStorage.getItem(`summary_${auditId}`);
    const cachedFileResult = sessionStorage.getItem(`fileResult_${auditId}`);
    if (cachedFileResult) {
      try { setFileResult(JSON.parse(cachedFileResult)); } catch { /* ignore */ }
    }

    (async () => {
      try {
        const res = await getAudit(auditId);
        if (res.audit) {
          setAudit(res.audit);
          setPhase("results");
          return;
        }
        if (res.locked) {
          setSummary(res.summary);
          setPhase("gate");
          return;
        }
        // Very defensive fallback — treat cached summary as good enough.
        if (cachedSummary) {
          setSummary(JSON.parse(cachedSummary));
          setPhase("gate");
        } else {
          setPhase("expired");
        }
      } catch (e) {
        if (e.response?.status === 404) setPhase("expired");
        else if (cachedSummary) {
          setSummary(JSON.parse(cachedSummary));
          setPhase("gate");
        } else setPhase("error");
      }
    })();
  }, [auditId, location.state]);

  if (phase === "loading")
    return (
      <Centered>
        <Loader2 size={28} className="animate-spin text-[color:var(--green)]" data-testid="audit-loading" />
      </Centered>
    );

  if (phase === "expired") return <Expired />;

  if (phase === "error")
    return (
      <Centered>
        <div className="text-center max-w-md" data-testid="audit-error">
          <h1 className="font-display text-3xl text-[color:var(--ink)]">Something went wrong</h1>
          <p className="mt-4 text-[color:var(--grey)]">
            Try refreshing — or email us at{" "}
            <a href="mailto:hello@sellersside.com" className="text-[color:var(--green)] underline underline-offset-4">hello@sellersside.com</a>{" "}
            and we'll rebuild it manually.
          </p>
        </div>
      </Centered>
    );

  if (phase === "gate" && summary)
    return (
      <Gate
        auditId={auditId}
        summary={summary}
        fileResult={fileResult}
        brandName={brandName}
        onUnlocked={(a) => {
          setAudit(a);
          setBrandName(a.meta?.inferredBrand?.value || brandName);
          setPhase("results");
        }}
      />
    );

  return <ResultsView audit={audit} setAudit={setAudit} brandName={brandName} />;
}
